import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Settings, Users, Trophy } from 'lucide-react';
import LeagueSettingsOverlay from './LeagueSettingsOverlay';

interface LeagueLeaderboardViewProps {
  leagueName: string;
  leagueColor: 'orange' | 'blue';
  onBack: () => void;
}

interface LeaderboardEntry {
  rank: number;
  username: string;
  points: number;
  isCurrentUser?: boolean;
  isWinner?: boolean;
}

const LeagueLeaderboardView: React.FC<LeagueLeaderboardViewProps> = ({
  leagueName,
  leagueColor: _leagueColor,
  onBack,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'today' | 'week' | 'month' | 'season'>('week');
  const [selectedWeek, setSelectedWeek] = useState(t('leaderboard.weekLabel', { n: 16 }));
  const [showSettings, setShowSettings] = useState(false);

  const tabs = [
    { id: 'today' as const, label: t('leaderboard.today') },
    { id: 'week' as const, label: t('leaderboard.week') },
    { id: 'month' as const, label: t('leaderboard.month') },
    { id: 'season' as const, label: t('leaderboard.season') },
  ];

  const weeks = [16, 15, 14, 13, 12, 11].map(n => t('leaderboard.weekLabel', { n }));

  const leaderboardData: LeaderboardEntry[] = [
    {
      rank: 153,
      username: '{Username}',
      points: 75,
      isCurrentUser: true,
    },
    {
      rank: 1,
      username: 'Carl Harrison',
      points: 82,
      isWinner: true,
    },
    { rank: 2, username: 'Hannah Reid', points: 75 },
    { rank: 3, username: 'Amelia Taylor', points: 37 },
    { rank: 4, username: 'Marcus Korsgaard', points: 35 },
    { rank: 5, username: 'Luck Marty', points: 32 },
    { rank: 6, username: 'Desirae Calzoni', points: 31 },
    { rank: 7, username: 'Phil Williams', points: 28 },
    { rank: 8, username: 'Edi Carlo', points: 27 },
    { rank: 9, username: 'Ellie Smiths', points: 26 },
  ];

  return (
    <div data-test-id="league-leaderboard-view" className="h-full flex flex-col relative">
      {/* Header */}
      <header className="bg-background border-b border-border/30 p-4">
        <div className="flex items-center justify-between">
          <button
            data-test-id="back-to-leagues-btn"
            onClick={onBack}
            className="flex items-center gap-2 text-foreground hover:text-muted-foreground transition-smooth"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t('common.back')}</span>
          </button>

          <button
            data-test-id="open-league-settings-btn"
            onClick={() => setShowSettings(true)}
            className="p-2 text-foreground hover:text-muted-foreground transition-smooth"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* League Info */}
      <div className="px-4 py-4 text-center border-b border-border/30">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Users className="w-5 h-5 text-muted-foreground" />
        </div>
        <h1 className="text-foreground font-bold text-xl">{leagueName}</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Tab Navigation */}
        <div className="px-4 py-4">
          <div className="flex space-x-1 bg-surface rounded-xl p-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-test-id={`period-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-smooth
                  ${
                    activeTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Week Selection */}
        {activeTab === 'week' && (
          <div className="px-4 pb-4">
            <div className="flex gap-2 overflow-x-auto">
              {weeks.map(week => (
                <button
                  key={week}
                  data-test-id={`week-selector-${week.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setSelectedWeek(week)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-smooth
                    ${
                      selectedWeek === week
                        ? 'bg-white text-gray-900'
                        : 'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  {week}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Leaderboard Header */}
        <div className="px-4 pb-2">
          <div className="flex items-center justify-between text-muted-foreground text-sm font-medium">
            <div className="flex items-center gap-4">
              <span>{t('leaderboard.rank')}</span>
              <span>{t('leaderboard.user')}</span>
            </div>
            <span>{t('leaderboard.points')}</span>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="px-4 pb-6">
          <div className="space-y-2">
            {leaderboardData.map(player => (
              <div
                key={`${player.rank}-${player.username}`}
                data-test-id={`league-leaderboard-row-${player.rank}`}
                data-test-state={player.isCurrentUser ? 'current-user' : 'other-user'}
                className={`
                  rounded-xl p-4 border-0 overflow-hidden
                  ${player.isCurrentUser ? 'bg-primary text-white' : 'bg-surface'}
                `}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-4">
                    <span className="font-bold text-lg">{player.rank}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{player.username}</span>
                      {player.isWinner && (
                        <Trophy
                          data-test-id={`trophy-icon-${player.rank}`}
                          className="w-4 h-4 text-yellow-400"
                        />
                      )}
                    </div>
                  </div>
                  <span className="font-bold text-lg">{player.points}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Settings Overlay */}
      <LeagueSettingsOverlay
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        leagueName={leagueName}
      />
    </div>
  );
};

export default LeagueLeaderboardView;
